/**
 * 数据表格卡片组件
 *
 * 用卡片容器包装的数据表格，支持空状态展示和移动端适配。
 */

import type { CardProps } from 'antd';
import { Card, Empty, Table } from 'antd';
import type { ColumnsType, TableProps } from 'antd/es/table';
import type React from 'react';

/**
 * DataTableCard 组件属性
 */
export interface DataTableCardProps<T> {
  /** 卡片标题 */
  title?: React.ReactNode;
  /** 卡片右上角额外内容 */
  extra?: React.ReactNode;
  /** 表格列定义 */
  columns: ColumnsType<T>;
  /** 表格数据 */
  dataSource: T[];
  /** 行唯一标识字段，默认 'id' */
  rowKey?: string | ((record: T) => string);
  /** 加载中 */
  loading?: boolean;
  /** 空状态描述 */
  emptyText?: string;
  /** 表格最大高度（启用垂直滚动） */
  maxHeight?: number;
  /** 表格其他属性（可覆盖默认的 pagination={false}、size="small" 等） */
  tableProps?: Omit<
    TableProps<T>,
    'columns' | 'dataSource' | 'loading' | 'rowKey'
  >;
  /** 卡片其他属性 */
  cardProps?: Omit<CardProps, 'title' | 'extra'>;
  /** 自定义样式 */
  style?: React.CSSProperties;
  /** 自定义类名 */
  className?: string;
}

/**
 * 数据表格卡片组件
 *
 * @example
 * ```tsx
 * <DataTableCard
 *   title="最近订单"
 *   extra={<Button type="link">查看全部</Button>}
 *   columns={orderColumns}
 *   dataSource={recentOrders}
 *   loading={loading}
 *   emptyText="暂无订单数据"
 *   maxHeight={400}
 * />
 * ```
 */
export function DataTableCard<T extends object>({
  title,
  extra,
  columns,
  dataSource,
  rowKey = 'id',
  loading = false,
  emptyText = '暂无数据',
  maxHeight,
  tableProps,
  cardProps,
  style,
  className,
}: DataTableCardProps<T>): React.ReactElement {
  return (
    <Card
      title={title}
      extra={extra}
      style={style}
      className={className}
      {...cardProps}
    >
      <Table<T>
        columns={columns}
        dataSource={dataSource}
        rowKey={rowKey}
        loading={loading}
        pagination={false}
        size="small"
        locale={{
          emptyText: (
            <Empty
              description={emptyText}
              image={Empty.PRESENTED_IMAGE_SIMPLE}
            />
          ),
        }}
        scroll={
          maxHeight ? { y: maxHeight, x: 'max-content' } : { x: 'max-content' }
        }
        {...tableProps}
      />
    </Card>
  );
}
